import type { FC, MouseEvent } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import YouTube from '@mui/icons-material/YouTube';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import { useAnchorMenu } from 'hooks';

interface GoogleVideoOpenMenuProps {
  sourceVideoUrl?: string;
  storedVideoUrl?: string;
  position?: 'top' | 'bottom';
  downloadProgress?: number | null;
  onOpenChange?: (open: boolean) => void;
  onDownloadVideo: () => void;
}

const btnWhite = { color: 'white', fontSize: 10 } as const;
const iconSx = { mr: -0.5 } as const;

const GoogleVideoOpenMenu: FC<GoogleVideoOpenMenuProps> = ({
  sourceVideoUrl,
  storedVideoUrl,
  position = 'bottom',
  downloadProgress = null,
  onOpenChange,
  onDownloadVideo,
}) => {
  const menu = useAnchorMenu();
  const isDownloading = downloadProgress !== null;

  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    menu.handleOpenMenu(e);
    onOpenChange?.(true);
  };

  const handleCloseMenu = () => {
    menu.handleCloseMenu();
    onOpenChange?.(false);
  };

  const handleOpenUrl = (url?: string) => () => {
    if (!url) return;
    window.open(url, '_blank');
    handleCloseMenu();
  };

  return (
    <>
      <Button
        onClick={handleOpenMenu}
        variant="text"
        size="small"
        startIcon={<LaunchOutlined sx={iconSx} />}
        disabled={!sourceVideoUrl && !storedVideoUrl}
        sx={btnWhite}
      >
        Open
      </Button>
      <Menu
        anchorEl={menu.anchor}
        open={menu.isOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: position === 'bottom' ? 'top' : 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: position === 'bottom' ? 'bottom' : 'top', horizontal: 'center' }}
      >
        <MenuItem disabled={!sourceVideoUrl} onClick={handleOpenUrl(sourceVideoUrl)}>
          <ListItemIcon>
            <YouTube fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open YT</ListItemText>
        </MenuItem>
        <MenuItem disabled={!storedVideoUrl} onClick={handleOpenUrl(storedVideoUrl)}>
          <ListItemIcon>
            <PlayCircleOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open video</ListItemText>
        </MenuItem>
        <MenuItem disabled={!storedVideoUrl || isDownloading} onClick={onDownloadVideo}>
          <ListItemIcon>
            <DownloadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{isDownloading ? 'Downloading...' : 'Download'}</ListItemText>
          {isDownloading && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {downloadProgress}%
            </Typography>
          )}
        </MenuItem>
      </Menu>
    </>
  );
};

export default GoogleVideoOpenMenu;
